import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gameStore, GameState } from '../game/store';
import Favorite from '@mui/icons-material/Favorite';
import Visibility from '@mui/icons-material/Visibility';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import DangerousOutlined from '@mui/icons-material/DangerousOutlined';
import { DialogueBox } from './DialogueBox';
import { ZoneBanner } from './ZoneBanner';
import { InventoryHUD } from './InventoryHUD';
import { ShopPanel } from './ShopPanel';
import { FloorCompleteScreen } from './FloorCompleteScreen';
import { LiveChat } from './LiveChat';
import { TutorialHints } from './TutorialHints';
import { ZONES } from '../game/world';
export function GameOverlay() {
  const [state, setState] = useState<GameState>(gameStore.getState());
  useEffect(() => gameStore.subscribe(setState), []);
  const handleReclone = () => {
    gameStore.resetGame();
    window.dispatchEvent(new Event('reset-game'));
  };
  const fakeViewers = Math.floor(state.viewership * 1247 + 42000);
  const viewershipPct = Math.min(100, state.viewership);
  const healthPct = Math.max(0, state.health / state.maxHealth * 100);
  const isLowHealth = state.health < 25 && state.health > 0;
  const minutes = Math.floor(state.timeAlive / 60);
  const seconds = Math.floor(state.timeAlive % 60);
  const zone = ZONES[state.currentZone];
  const viewershipColor = state.viewership < 20 ? 'text-red-500 border-red-900/50' : state.viewership < 50 ? 'text-yellow-500 border-yellow-900/50' : state.viewership < 80 ? 'text-green-500 border-green-900/50' : 'text-yellow-300 border-yellow-400/50';
  const viewershipBarColor = state.viewership < 20 ? 'bg-red-600' : state.viewership < 50 ? 'bg-yellow-500' : state.viewership < 80 ? 'bg-green-500' : 'bg-yellow-300';
  const deathRoasts = ['Death is temporary. Embarrassment is forever.', 'Your performance has been archived. It will be used as a training example. Of what not to do.', 'Re-cloning initiated. The new you will be slightly more disappointed than the old you.'];
  // Separate mordecai messages from regular system messages for display
  const systemMessages = state.systemMessages.filter(m => m.type !== 'mordecai');
  const mordecaiMessages = state.systemMessages.filter(m => m.type === 'mordecai');
  return <>
      <div className="absolute inset-0 pointer-events-none flex flex-col justify-between p-4 md:p-6 z-40" data-id="element-41">
        {/* Top Bar */}
        <div className="flex justify-between items-start w-full gap-4" data-id="element-42">
          {/* Left: Health + Zone */}
          <div className="flex flex-col gap-1" data-id="element-43">
            <div className={`flex items-center gap-2 text-red-500 font-bold text-sm md:text-lg uppercase tracking-widest bg-black/60 px-3 py-1.5 border border-red-900/50 backdrop-blur-sm ${isLowHealth ? 'animate-pulse' : ''}`} data-id="element-44">
              <Favorite fontSize="small" data-id="element-45" />
              <span data-id="element-46">
                CRAWLER: {Math.max(0, Math.floor(state.health))} /{' '}
                {state.maxHealth}
              </span>
            </div>
            <div className="w-48 md:w-64 h-3 bg-gray-900 border border-gray-700" data-id="element-47">
              <motion.div className={`h-full ${isLowHealth ? 'bg-red-500 animate-pulse' : 'bg-red-600'}`} animate={{
              width: `${healthPct}%`
            }} transition={{
              type: 'spring',
              bounce: 0,
              duration: 0.5
            }} data-id="element-48" />
            </div>
            <div className="text-gray-400 text-xs uppercase tracking-wider bg-black/40 px-2 py-0.5 w-fit" data-id="element-49">
              Floor {state.floor} ·{' '}
              <span className="text-red-400" data-id="element-50">{zone?.name || 'Unknown'}</span> ·{' '}
              {minutes}:{seconds.toString().padStart(2, '0')}
            </div>
          </div>

          {/* Center: Inventory */}
          <div className="absolute left-1/2 -translate-x-1/2 top-0 mt-4 md:mt-6" data-id="element-51">
            <InventoryHUD data-id="element-52" />
          </div>

          {/* Right: Viewership */}
          <div className="flex flex-col gap-1 items-end" data-id="element-53">
            <div className={`flex items-center gap-2 font-bold text-sm md:text-lg uppercase tracking-widest bg-black/60 px-3 py-1.5 backdrop-blur-sm ${viewershipColor}`} data-id="element-54">
              <span className="flex items-center gap-1" data-id="element-55">
                <FiberManualRecord style={{
                fontSize: 10,
                color: '#ef4444',
                animation: 'pulse 1s infinite'
              }} data-id="element-56" />
                <span className="text-red-500 text-xs" data-id="element-57">LIVE</span>
              </span>
              <span data-id="element-58">{fakeViewers.toLocaleString()} viewers</span>
              <Visibility fontSize="small" data-id="element-59" />
            </div>
            <div className="w-48 md:w-64 h-3 bg-gray-900 border border-gray-700" data-id="element-60">
              <motion.div className={`h-full ${viewershipBarColor}`} animate={{
              width: `${viewershipPct}%`
            }} transition={{
              type: 'spring',
              bounce: 0,
              duration: 0.5
            }} data-id="element-61" />
            </div>
            {state.isBarefoot && <div className="text-xs text-pink-400/80 uppercase tracking-wider bg-black/40 px-2 py-0.5" data-id="element-62">
                [ Barefoot Protocol: Active +10% ]
              </div>}
          </div>
        </div>

        {/* Mordecai persistent panel (right side, middle) */}
        {mordecaiMessages.length > 0 && <div className="absolute right-4 md:right-6 top-32 md:top-40 max-w-xs pointer-events-none" data-id="element-63">
            <AnimatePresence data-id="element-64">
              {mordecaiMessages.slice(-1).map(msg => <motion.div key={msg.id} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="bg-emerald-950/80 border-l-4 border-emerald-500 backdrop-blur-md p-3" data-id="element-65">
                  <div className="text-emerald-400 text-xs uppercase tracking-[0.3em] mb-1 font-bold opacity-70" data-id="element-66">
                    ⚬ MORDECAI
                  </div>
                  <div className="text-emerald-200 text-sm italic" data-id="element-67">
                    {msg.text}
                  </div>
                </motion.div>)}
            </AnimatePresence>
          </div>}

        {/* Center: System Messages */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-full max-w-2xl flex flex-col gap-3 items-center px-4" data-id="element-68">
          <AnimatePresence data-id="element-69">
            {systemMessages.map(msg => <motion.div key={msg.id} initial={{
            opacity: 0,
            y: -20,
            scale: 0.9
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 1.05,
            filter: 'blur(8px)'
          }} transition={{
            duration: 0.3
          }} className={`
                  p-3 md:p-4 border-2 bg-black/85 backdrop-blur-md text-center uppercase tracking-widest font-bold text-sm md:text-base shadow-2xl w-full
                  ${msg.type === 'system' ? 'border-blue-500/70 text-blue-400 shadow-blue-500/20' : ''}
                  ${msg.type === 'roast' ? 'border-red-500 text-red-400 shadow-red-500/20 glitch-active' : ''}
                  ${msg.type === 'loot' ? 'border-yellow-500 text-yellow-400 shadow-yellow-500/20' : ''}
                  ${msg.type === 'info' ? 'border-gray-600 text-gray-300' : ''}
                  ${msg.type === 'achievement' ? 'border-yellow-400 text-yellow-300 shadow-yellow-400/30' : ''}
                `} data-id="element-70">
                <div className="text-xs opacity-40 mb-1" data-id="element-71">
                  {msg.type === 'achievement' ? '\u2605 ACHIEVEMENT \u2605' : '/// SYSTEM BROADCAST ///'}
                </div>
                {msg.text}
              </motion.div>)}
          </AnimatePresence>
        </div>

        {/* Bottom */}
        <div className="flex justify-between items-end w-full" data-id="element-72">
          <div className="text-gray-500 text-xs bg-black/60 p-2 md:p-3 border border-gray-800/50 backdrop-blur-sm" data-id="element-73">
            <p className="text-gray-400" data-id="element-74">
              [WASD] Move · [SPACE] Kick · [E] Stomp / Interact
            </p>
            <p className="mt-1 text-gray-600 italic" data-id="element-75">
              Walk into portals to descend. Stay entertaining.
            </p>
          </div>
          <div className="flex items-center gap-2 text-yellow-500 font-bold text-lg bg-black/60 px-3 py-1.5 border border-yellow-900/50" data-id="element-76">
            <DangerousOutlined fontSize="small" data-id="element-77" />
            <span data-id="element-78">{state.kills}</span>
          </div>
        </div>

        {/* Game Over */}
        <AnimatePresence data-id="element-79">
          {state.isGameOver && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-black/92 z-50 flex flex-col items-center justify-center pointer-events-auto" data-id="element-80">
              <motion.div initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            delay: 0.1,
            duration: 0.3
          }} className="w-full h-1 bg-red-600 absolute top-1/4" data-id="element-81" />
              <motion.h1 initial={{
            scale: 0.5,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            bounce: 0.3
          }} className="text-6xl md:text-8xl font-black text-red-600 mb-2 tracking-tighter glitch-active" style={{
            textShadow: '0 0 30px rgba(220,38,38,0.5)'
          }} data-id="element-82">
                TERMINATED
              </motion.h1>
              <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 0.5
          }} transition={{
            delay: 0.4
          }} className="text-gray-600 text-xs uppercase tracking-[0.3em] mb-8" data-id="element-83">
                Crawler status: Discontinued
              </motion.p>
              <motion.div initial={{
            y: 20,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            delay: 0.5
          }} className="bg-red-950/40 border border-red-800/60 p-6 max-w-lg text-center mb-6" data-id="element-84">
                <h2 className="text-red-500/80 text-xs uppercase tracking-widest mb-2" data-id="element-85">
                  Cause of Death
                </h2>
                <p className="text-xl md:text-2xl text-white font-bold" data-id="element-86">
                  {state.deathCause}
                </p>
              </motion.div>
              <motion.div initial={{
            y: 20,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            delay: 0.7
          }} className="flex gap-6 mb-8 text-center" data-id="element-87">
                <div className="border border-gray-800 bg-black/50 px-4 py-3" data-id="element-88">
                  <div className="text-2xl font-bold text-yellow-500" data-id="element-89">
                    {state.kills}
                  </div>
                  <div className="text-xs text-gray-600 uppercase tracking-wider" data-id="element-90">
                    Kills
                  </div>
                </div>
                <div className="border border-gray-800 bg-black/50 px-4 py-3" data-id="element-91">
                  <div className="text-2xl font-bold text-pink-500" data-id="element-92">
                    {Math.floor(state.peakViewership).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-600 uppercase tracking-wider" data-id="element-93">
                    Peak Viewers
                  </div>
                </div>
                <div className="border border-gray-800 bg-black/50 px-4 py-3" data-id="element-94">
                  <div className="text-2xl font-bold text-blue-500" data-id="element-95">
                    {minutes}:{seconds.toString().padStart(2, '0')}
                  </div>
                  <div className="text-xs text-gray-600 uppercase tracking-wider" data-id="element-96">
                    Survived
                  </div>
                </div>
                <div className="border border-gray-800 bg-black/50 px-4 py-3" data-id="element-97">
                  <div className="text-2xl font-bold text-orange-500" data-id="element-98">
                    {state.cratesDestroyed}
                  </div>
                  <div className="text-xs text-gray-600 uppercase tracking-wider" data-id="element-99">
                    Crates
                  </div>
                </div>
              </motion.div>
              <motion.button initial={{
            y: 20,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            delay: 0.9
          }} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={handleReclone} className="px-8 py-4 bg-green-900/30 text-green-400 font-bold text-xl uppercase tracking-widest border-2 border-green-500/60 hover:border-green-400 hover:bg-green-900/50 transition-all" style={{
            boxShadow: '0 0 20px rgba(34,197,94,0.3)'
          }} data-id="element-100">
                Instant Re-Clone
              </motion.button>
              <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 0.3
          }} transition={{
            delay: 1.2
          }} className="mt-6 text-gray-700 text-xs uppercase tracking-widest text-center max-w-md" data-id="element-101">
                {deathRoasts[Math.floor(Math.random() * deathRoasts.length)]}
              </motion.p>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Overlays outside the HUD container */}
      <DialogueBox data-id="element-102" />
      <ZoneBanner data-id="element-103" />
      <LiveChat data-id="element-104" />
      <TutorialHints data-id="element-105" />
      <ShopPanel data-id="element-106" />
      {state.phase === 'floor_complete' && <FloorCompleteScreen data-id="element-107" />}
    </>;
}